import { useState, useEffect, useRef, useCallback } from "react";
import { Volume2, Pause, Play, RotateCcw, Gauge } from "lucide-react";

const SPEEDS = [1, 1.25, 1.5, 0.75];

const ttsSupported = () =>
  typeof window !== "undefined" &&
  "speechSynthesis" in window &&
  typeof window.SpeechSynthesisUtterance !== "undefined";

const pickEnglishVoice = (voices) => {
  if (!voices || !voices.length) return null;
  const preferred = [
    "Google US English",
    "Samantha",
    "Microsoft Aria Online (Natural) - English (United States)",
    "Microsoft Jenny Online (Natural) - English (United States)",
    "Daniel",
    "Karen",
  ];
  for (const name of preferred) {
    const v = voices.find((x) => x.name === name);
    if (v) return v;
  }
  return (
    voices.find((v) => /^en[-_]US/i.test(v.lang)) ||
    voices.find((v) => /^en/i.test(v.lang)) ||
    voices[0]
  );
};

// Split into sentence-sized chunks. Chunking makes pause/resume reliable on iOS/WKWebView
// (where native pause()/resume() is flaky) and avoids iOS's long-utterance cut-offs.
const splitSentences = (t) =>
  (t || "")
    .split(/\n+/)
    .flatMap((line) => line.match(/[^.!?]+[.!?]*\s*/g) || [line])
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * "Listen to the forecast" — reads the provided plain text aloud using the browser's
 * built-in speech synthesis. Play/pause, restart and playback-speed controls. Never autoplays.
 * Pause/resume is implemented via sentence chunks (resume restarts the current sentence),
 * which is robust across Safari / iOS WKWebView.
 */
export const ForecastListen = ({ text }) => {
  const supported = ttsSupported();
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [rate, setRate] = useState(1);

  const voiceRef = useRef(null);
  const rateRef = useRef(1);
  const chunksRef = useRef([]);
  const idxRef = useRef(0);
  const runIdRef = useRef(0); // invalidates stale utterance callbacks after cancel

  useEffect(() => {
    if (!supported) return;
    const load = () => {
      voiceRef.current = pickEnglishVoice(window.speechSynthesis.getVoices());
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [supported]);

  const stopAll = useCallback(() => {
    if (!supported) return;
    runIdRef.current += 1; // any in-flight onend becomes a no-op
    window.speechSynthesis.cancel();
    idxRef.current = 0;
    setPlaying(false);
    setPaused(false);
  }, [supported]);

  // Rebuild chunks when the forecast changes; stop on change and on unmount (no stale/overlapping audio)
  useEffect(() => {
    if (!supported) return;
    chunksRef.current = splitSentences(text);
    stopAll();
    return () => stopAll();
  }, [text, supported, stopAll]);

  const speakChunk = useCallback(
    (i, myRun) => {
      if (myRun !== runIdRef.current) return; // sequence was superseded
      const chunks = chunksRef.current;
      if (i >= chunks.length) {
        setPlaying(false);
        setPaused(false);
        return;
      }
      idxRef.current = i;
      const u = new window.SpeechSynthesisUtterance(chunks[i]);
      if (voiceRef.current) {
        u.voice = voiceRef.current;
        u.lang = voiceRef.current.lang;
      } else {
        u.lang = "en-US";
      }
      u.rate = rateRef.current;
      u.pitch = 1;
      u.onend = () => {
        if (myRun !== runIdRef.current) return;
        speakChunk(i + 1, myRun);
      };
      u.onerror = () => {
        if (myRun !== runIdRef.current) return;
        setPlaying(false);
        setPaused(false);
      };
      window.speechSynthesis.speak(u);
    },
    []
  );

  // Start a fresh narration sequence from chunk `from`
  const startFrom = useCallback(
    (from) => {
      if (!supported || !chunksRef.current.length) return;
      runIdRef.current += 1;
      const myRun = runIdRef.current;
      window.speechSynthesis.cancel(); // clear queue, no overlap
      setPlaying(true);
      setPaused(false);
      // slight delay lets cancel() settle on iOS before speak()
      setTimeout(() => speakChunk(from, myRun), 60);
    },
    [supported, speakChunk]
  );

  const handlePlayPause = () => {
    if (!supported) return;
    if (!playing) {
      startFrom(0);
    } else if (paused) {
      startFrom(idxRef.current); // resume = restart current sentence (WKWebView-safe)
    } else {
      runIdRef.current += 1; // stop advancing
      window.speechSynthesis.cancel();
      setPaused(true); // keep idxRef so we can resume this sentence
    }
  };

  const handleRestart = () => {
    if (!supported) return;
    startFrom(0);
  };

  const handleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(rate) + 1) % SPEEDS.length];
    setRate(next);
    rateRef.current = next;
    if (playing && !paused) startFrom(idxRef.current); // apply new rate from current sentence
  };

  if (!supported) {
    return (
      <span className="text-white/40 text-xs" data-testid="forecast-listen-unsupported">
        Audio narration isn’t supported in this browser.
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid="forecast-listen" role="group" aria-label="Listen to the forecast">
      <button
        onClick={handlePlayPause}
        data-testid="forecast-listen-toggle"
        aria-label={!playing ? "Listen to the forecast" : paused ? "Resume narration" : "Pause narration"}
        className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
      >
        {!playing ? (
          <><Volume2 className="w-4 h-4" /> Listen</>
        ) : paused ? (
          <><Play className="w-4 h-4" /> Resume</>
        ) : (
          <><Pause className="w-4 h-4" /> Pause</>
        )}
      </button>

      <button
        onClick={handleRestart}
        disabled={!playing}
        data-testid="forecast-listen-restart"
        aria-label="Restart narration"
        className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
      >
        <RotateCcw className="w-4 h-4" />
      </button>

      <button
        onClick={handleSpeed}
        data-testid="forecast-listen-speed"
        aria-label={`Playback speed ${rate}×. Tap to change.`}
        className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
      >
        <Gauge className="w-3.5 h-3.5" /> {rate}×
      </button>
    </div>
  );
};
